/**
 * Message protocol between the main-thread SchedulerProxy and the
 * Dedicated Worker that runs PatternPlayer.
 *
 * Clock sync: main sends AudioContext `audioNow` only. The worker stamps its
 * own `performance.now()` on receipt — Window and Dedicated Worker have
 * different `performance.timeOrigin`, so never mix their performance clocks.
 */
import type { ClipIR } from "../clip/types";
import type { PlayheadInfo } from "../clip/patternPlayer";
import type { KernelMessage } from "../types";

export type SchedulerMainToWorker =
  | { type: "init"; lookahead: number; intervalMs: number }
  | { type: "clockSync"; audioNow: number }
  | {
      type: "play";
      clip: ClipIR;
      startStep?: number;
      audioNow: number;
      /** Invalidates in-flight kernel msgs from a previous play. */
      scheduleGen: number;
    }
  | { type: "stop"; scheduleGen: number }
  /** Live clip edit while playing — `scheduleGen` invalidates in-flight lookahead. */
  | { type: "setClip"; clip: ClipIR; scheduleGen?: number }
  | { type: "setMuteSolo"; muted?: boolean[]; solo?: boolean[] }
  | { type: "setOpenHoldDefaultSlots"; flags: boolean[] }
  | { type: "dispose" };

export type SchedulerWorkerToMain =
  | { type: "ready" }
  | { type: "kernel"; message: KernelMessage; scheduleGen: number }
  | { type: "playhead"; info: PlayheadInfo | undefined }
  | { type: "ended" }
  /** Worker finished stop + posted silence — main may allNotesOff again. */
  | { type: "stopped" }
  | { type: "error"; message: string };

/**
 * Extrapolate AudioContext time from a sync sample.
 * `syncPerformanceNow` must be the worker's `performance.now()` at receipt.
 */
export function audioTimeFromSync(
  syncAudioNow: number,
  syncPerformanceNow: number,
  performanceNowMs: number = performance.now(),
): number {
  return syncAudioNow + (performanceNowMs - syncPerformanceNow) * 0.001;
}

/**
 * Whether a worker→main kernel message should hit the transport.
 * Drops lookahead note traffic from an older play (or after stop).
 */
export function acceptWorkerKernel(
  playing: boolean,
  currentGen: number,
  messageGen: number,
  messageType: KernelMessage["type"],
): boolean {
  if (messageType === "allNotesOff") {
    return true;
  }
  if (messageGen !== currentGen) {
    return false;
  }
  if (
    !playing
    && (messageType === "noteOn"
      || messageType === "noteOff"
      || messageType === "setNoteFx")
  ) {
    return false;
  }
  return true;
}
